import * as anchor from '@coral-xyz/anchor'
import { Connection, PublicKey, SystemProgram, LAMPORTS_PER_SOL, SYSVAR_CLOCK_PUBKEY, SYSVAR_STAKE_HISTORY_PUBKEY, SYSVAR_RENT_PUBKEY, Transaction, VersionedTransaction, TransactionMessage } from '@solana/web3.js'
import { WalletContextState } from '@solana/wallet-adapter-react'
import { TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID, getAssociatedTokenAddressSync } from '@solana/spl-token'
import BN from 'bn.js'
import { Buffer } from 'buffer'
// Import the actual IDL generated by Anchor build (V3.1 - copied to app/src/idl for Vite build compatibility)
import IDL from '../idl/cto_pools.json'

/**
 * ========================================================================
 *  V3.1 TypeScript interfaces matching the Rust program account structures
 *  These ensure type safety when working with on-chain data
 * ========================================================================
 */

/**
 * V3.1 Pool account structure
 * Includes LST-based accounting, PumpSwap configuration, and reentrancy guard
 */
export interface Pool {
  tokenMint: PublicKey
  authority: PublicKey
  creator: PublicKey
  totalShares: BN
  // LST-based accounting
  totalPoolTokens: BN      // Total jitoSOL tokens held by pool
  reservedPoolTokens: BN   // jitoSOL reserved for pending proposal payouts
  totalSpentLamports: BN   // Cumulative lamports spent from treasury
  protocolFeeBps: number
  quorumBps: number
  minProposerDepositLamports: BN
  devFeeWallet: PublicKey
  burnTokenMint: PublicKey
  activeProposal: PublicKey | null
  proposalCount: BN
  proposalCooldownUntilTs: BN  // Cooldown timestamp after abort
  basePenaltyLamports: BN     // Base penalty for proposers
  // Recovery proposal support
  activeRecovery: PublicKey | null
  recoveryCount: BN
  // Stake pool configuration
  stakePoolProgram: PublicKey
  stakePool: PublicKey
  lstMint: PublicKey
  // V3.1: PumpSwap configuration
  pumpswapEnabled: boolean
  pumpswapPoolId: PublicKey
  pumpswapBaseVault: PublicKey
  pumpswapQuoteVault: PublicKey
  pumpswapGlobalConfig: PublicKey
  pumpswapFeeRecipient: PublicKey
  pumpswapVersion: number       // V3.1: PumpSwap version tracking
  // Raydium swap config (legacy)
  raydiumPoolId: PublicKey
  raydiumEnabled: boolean
  // V3.1: Reentrancy guard
  locked: boolean               // V3.1: Reentrancy lock flag
}

/**
 * V3.1 Donor account structure
 * Includes abort strike tracking for governance safety
 */
export interface Donor {
  pool: PublicKey
  wallet: PublicKey
  shares: BN
  totalDepositedLamports: BN
  lastSharesChangeSlot: BN
  // V3.1: Strike tracking for abort votes
  abortStrikeCount: number       // Strikes for voting abort on passing proposals
  proposeStrikeCount: number     // Strikes for having proposals aborted
  nonAbortParticipationCount: number  // Counter for strike reset (abort)
  nonProposeParticipationCount: number // Counter for strike reset (propose)
}

/**
 * V3.1 Proposal account structure
 * Includes abort voting support
 */
export interface Proposal {
  pool: PublicKey
  kind: { payout: {} }              // ProposalKind enum
  proposerWallet: PublicKey         // V3.1: Added proposer tracking
  requestedLamports: BN
  destinationWallet: PublicKey
  title: string
  description: string
  createdAtTs: BN
  deadlineTs: BN
  snapshotSlot: BN
  totalSnapshotShares: BN
  lockedPoolTokens: BN              // jitoSOL locked for this proposal
  yesWeight: BN
  noWeight: BN
  abstainWeight: BN
  participationWeight: BN
  // V3.1: Abort voting fields
  abortVoter1: PublicKey           // First abort voter wallet
  abortVoter2: PublicKey           // Second abort voter wallet
  abortCount: number               // Number of abort votes (0-2)
  status: { active: {} } | { failed: {} } | { executed: {} } | { aborted: {} }  // V3.1: Added Aborted status
}

/**
 * V3.1 Recovery Proposal account structure
 * Now includes abort voting support (same as regular proposals)
 */
export interface RecoveryProposal {
  pool: PublicKey
  tokenMint: PublicKey
  requestedAmount: BN
  destinationWallet: PublicKey
  title: string
  description: string
  createdAtTs: BN
  deadlineTs: BN
  snapshotSlot: BN
  totalSnapshotShares: BN
  yesWeight: BN
  noWeight: BN
  abstainWeight: BN
  participationWeight: BN
  // V3.1: Abort voting fields for recovery proposals
  abortVoter1: PublicKey
  abortVoter2: PublicKey
  abortCount: number
  status: { active: {} } | { failed: {} } | { executed: {} } | { aborted: {} }
}

/**
 * Vote record structure
 */
export interface VoteRecord {
  proposal: PublicKey
  voter: PublicKey
  snapshotWeight: BN
  choice: { yes: {} } | { no: {} } | { abstain: {} } | { abort: {} }  // V3.1: Added Abort
  initialized: boolean
}

/**
 * V3.1: Vote choice enumeration matching the Rust enum
 * Added Abort vote option
 */
export enum VoteChoice {
  Yes = 'Yes',
  No = 'No', 
  Abstain = 'Abstain',
  Abort = 'Abort'   // V3.1: New abort vote option
}

/**
 * Contract deployment status interface
 * Provides information about the smart contract availability
 */
export interface ContractStatus {
  isDeployed: boolean
  error?: string
  fallbackMode: boolean
  lastChecked: number
}

/**
 * ========================================================================
 *  Program constants
 * ========================================================================
 */
export const PROTOCOL_FEE_BPS = 100
export const QUORUM_BPS = 3000
export const MIN_PROPOSER_DEPOSIT_LAMPORTS = LAMPORTS_PER_SOL

// Production wallet addresses for fee collection
const HARDCODED_DEV_FEE_WALLET = '9esi61m4t1WfKUD1mQrTz3FbAD9XiS4TbsVohiYc475S'
// CTOP token mint address for buy-and-burn mechanism (deployed to devnet)
const HARDCODED_BURN_TOKEN_MINT = 'E5HR8o4z4ceQTkPPi7GiVEhh5NWPbjDUDPFLm8ns45GZ'

/**
 * Jito Stake Pool Constants (Devnet)
 * These are the official Jito stake pool addresses for devnet
 */
export const JITO_STAKE_POOL_PROGRAM = new PublicKey('DPoo15wWDqpPJJtS2MUZ49aRxqz5ZaaJCJP4z8bLuib')
export const JITO_STAKE_POOL = new PublicKey('JitoY5pcAxWX6iyP2QdFwTznGb8A99PRCUCVVxB46WZ')
export const JITO_LST_MINT = new PublicKey('J1tos8mqbhdGcF3pgj4PCKyVjzWSURcpLZU7pPGHxSYi')

// Stake program address is constant on all networks
export const STAKE_PROGRAM_ID = new PublicKey('Stake11111111111111111111111111111111111111')

// V3.1: PumpSwap program address
export const PUMPSWAP_PROGRAM_ID = new PublicKey('pswapFCCXCemjnNJXqkqLz9xRQ7NmvMUv5rPdULFc79')

let _cachedProgramId: PublicKey | null = null

/**
 * Returns the CTO Pools program ID from IDL or environment variable
 * Caches the result for performance
 */
export function getCTOProgramId(): PublicKey {
  if (_cachedProgramId) {
    return _cachedProgramId
  }

  try {
    if (IDL.address && typeof IDL.address === 'string') {
      _cachedProgramId = new PublicKey(IDL.address)
      return _cachedProgramId
    }
  } catch (error) {
    console.warn('[anchorClient] Failed to load program ID from IDL:', error)
  }

  try {
    // @ts-ignore - Vite env types
    const envProgramId = import.meta?.env?.VITE_APP_PROGRAM_ID
    if (envProgramId && typeof envProgramId === 'string') {
      _cachedProgramId = new PublicKey(envProgramId)
      return _cachedProgramId
    }
  } catch (error) {
    console.warn('[anchorClient] Invalid VITE_APP_PROGRAM_ID:', error)
  }

  _cachedProgramId = SystemProgram.programId
  return _cachedProgramId
}

/**
 * Helper function to get a PublicKey from environment variable with fallback
 */
function getEnvPublicKey(envKey: string, fallback?: PublicKey): PublicKey {
  try {
    // @ts-ignore - Vite env types
    const envValue = import.meta?.env?.[envKey]
    
    if (envValue && typeof envValue === 'string' && envValue.trim().length > 0) {
      return new PublicKey(envValue)
    }
    
    let hardcodedValue: string | null = null
    
    switch (envKey) {
      case 'VITE_APP_DEV_FEE_WALLET':
        hardcodedValue = HARDCODED_DEV_FEE_WALLET
        break
      case 'VITE_APP_BURN_TOKEN_MINT':
        hardcodedValue = HARDCODED_BURN_TOKEN_MINT
        break
      case 'VITE_APP_STAKE_POOL_PROGRAM':
        return JITO_STAKE_POOL_PROGRAM
      case 'VITE_APP_STAKE_POOL':
        return JITO_STAKE_POOL
      case 'VITE_APP_LST_MINT':
        return JITO_LST_MINT
    }
    
    if (hardcodedValue) {
      return new PublicKey(hardcodedValue)
    }
    
    return fallback || SystemProgram.programId
    
  } catch (error) {
    return fallback || SystemProgram.programId
  }
}

/**
 * ========================================================================
 *  V3.1 Anchor client wrapper for CTO Pools program
 * ========================================================================
 */
export class CtoPoolsClient {
  public program: anchor.Program<any> | null
  public connection: Connection
  public wallet: WalletContextState
  public contractStatus: ContractStatus
  private mockDataCache: Map<string, any>

  constructor(connection: Connection, wallet: WalletContextState) {
    this.connection = connection
    this.wallet = wallet
    this.mockDataCache = new Map()
    this.contractStatus = {
      isDeployed: false,
      fallbackMode: true,
      lastChecked: 0
    }
    
    this.program = this.initializeProgram()
  }

  /**
   * Initializes the Anchor program with the IDL and provider
   */
  private initializeProgram(): anchor.Program<any> | null {
    try {
      const provider = new anchor.AnchorProvider(
        this.connection,
        this.wallet as any,
        { commitment: 'confirmed' }
      )
      
      const program = new anchor.Program(IDL as any, provider)
      return program
      
    } catch (error) {
      console.error('[anchorClient] Program initialization failed:', error)
      return null
    }
  }

  /**
   * Gets a fresh blockhash with confirmed commitment level
   * This is used to prevent "Blockhash not found" errors
   * Returns the blockhash and lastValidBlockHeight for transaction confirmation
   */
  private async getFreshBlockhash(): Promise<{ blockhash: string; lastValidBlockHeight: number }> {
    const { blockhash, lastValidBlockHeight } = await this.connection.getLatestBlockhash('confirmed')
    console.log('[anchorClient] Got fresh blockhash:', blockhash.substring(0, 16) + '...')
    return { blockhash, lastValidBlockHeight }
  }

  /**
   * Signs and sends a transaction with robust duplicate-safe handling.
   *
   * Why this is structured the way it is (root-cause fixes for a bug that manifested as
   * "Transaction simulation failed: This transaction has already been processed"):
   *
   *  1. We DO NOT re-fetch a blockhash here for a VersionedTransaction — the caller
   *     already baked a blockhash into the message at build time. Fetching another one
   *     (as the previous implementation did) wasted an RPC call and, worse, caused a
   *     mismatch between the signature we were about to compute and the blockhash we
   *     used to confirm it.
   *
   *  2. We set `skipPreflight: true` on send. We ALREADY simulate the transaction
   *     locally above; turning on preflight makes the RPC run a second simulation that
   *     sees the signature in its processed cache (or races with a wallet that
   *     auto-broadcasts) and throws "This transaction has already been processed".
   *
   *  3. We set `maxRetries: 0`. web3.js's internal retry loop re-sends the identical
   *     signed bytes; after the first send succeeds the second attempt hits the RPC's
   *     signature cache and throws the same duplicate-processed error. We do our own
   *     confirmation via polling instead.
   *
   *  4. If a send still reports "already processed" (e.g. a wallet adapter that
   *     broadcasts on sign), we recover by extracting the signature from the signed
   *     transaction bytes and confirming it — the transaction is genuinely on chain.
   */
  private async signAndSendTransaction(
    tx: Transaction | VersionedTransaction,
    options?: { lastValidBlockHeight?: number }
  ): Promise<string> {
    if (!this.wallet.publicKey || !this.wallet.signTransaction) {
      throw new Error('Wallet not connected or does not support signing')
    }

    // -- Determine the blockhash + validity window we will use for confirmation --
    // For VersionedTransaction: reuse the blockhash the caller already compiled into
    //   the message (mutating it post-compile would invalidate the message).
    // For legacy Transaction: we attach a fresh blockhash here.
    let blockhash: string
    let lastValidBlockHeight: number

    if (tx instanceof VersionedTransaction) {
      blockhash = tx.message.recentBlockhash
      if (options?.lastValidBlockHeight !== undefined) {
        lastValidBlockHeight = options.lastValidBlockHeight
      } else {
        // Solana blockhashes are valid for roughly 150 slots. Derive a safe upper
        // bound from the current block height so confirmTransaction has a timeout.
        try {
          const currentBlockHeight = await this.connection.getBlockHeight('confirmed')
          lastValidBlockHeight = currentBlockHeight + 150
        } catch {
          // Fallback: generous value so the confirmer polls for ~1 minute
          lastValidBlockHeight = Number.MAX_SAFE_INTEGER
        }
      }
    } else {
      const fresh = await this.getFreshBlockhash()
      blockhash = fresh.blockhash
      lastValidBlockHeight = fresh.lastValidBlockHeight
      tx.recentBlockhash = blockhash
      tx.feePayer = this.wallet.publicKey
    }

    // -- Local simulation (catch program errors early, avoid wallet UI warnings) --
    try {
      console.log('[anchorClient] Simulating transaction...')
      // simulateTransaction accepts both transaction types; overload disambiguates at runtime
      const simulation = await this.connection.simulateTransaction(tx as any)

      if (simulation.value.err) {
        console.error('[anchorClient] Simulation failed:', simulation.value.err)
        console.error('[anchorClient] Simulation logs:', simulation.value.logs)
        throw new Error(
          `Transaction simulation failed: ${JSON.stringify(simulation.value.err)} logs=${JSON.stringify(simulation.value.logs)}`
        )
      }

      console.log('[anchorClient] Simulation successful')
    } catch (error) {
      // Rethrow only true program-level simulation failures; swallow transient RPC errors
      if (error instanceof Error && error.message.includes('Transaction simulation failed')) {
        throw error
      }
      console.warn('[anchorClient] Simulation RPC error (proceeding with caution):', error)
    }

    // -- Sign --
    // @ts-ignore - WalletContextState.signTransaction is generic over both tx types at runtime
    const signedTx = await this.wallet.signTransaction(tx)

    // -- Send --
    // skipPreflight: true  -> we simulated locally; don't let the RPC double-simulate
    //                         (that's what was returning "already processed")
    // maxRetries: 0        -> don't let web3.js re-broadcast the same signed bytes
    let signature: string
    try {
      signature = await this.connection.sendRawTransaction(signedTx.serialize(), {
        skipPreflight: true,
        preflightCommitment: 'confirmed',
        maxRetries: 0,
      })
      console.log('[anchorClient] Transaction sent:', signature)
    } catch (sendError: any) {
      // Defensive recovery: if the tx is already on chain (wallet auto-broadcast etc.),
      // salvage the signature from the signed bytes and fall through to confirmation.
      const errMsg = (sendError?.message || '').toString()
      const alreadyProcessed =
        errMsg.includes('already been processed') || errMsg.includes('already processed')

      if (!alreadyProcessed) {
        throw sendError
      }

      const recovered = this.extractSignatureFromSignedTx(signedTx)
      if (!recovered) {
        throw sendError
      }
      signature = recovered
      console.warn(
        '[anchorClient] Send reported "already processed". Recovered signature and will confirm:',
        signature
      )
    }

    // -- Confirm --
    const confirmation = await this.connection.confirmTransaction(
      { signature, blockhash, lastValidBlockHeight },
      'confirmed'
    )

    if (confirmation.value.err) {
      throw new Error(`Transaction failed: ${JSON.stringify(confirmation.value.err)}`)
    }

    console.log('[anchorClient] Transaction confirmed:', signature)
    return signature
  }

  /**
   * Extract the base58-encoded signature (the first signer's signature) from a signed
   * transaction. Works for both legacy Transaction and VersionedTransaction. Used as a
   * recovery path when the network reports a transaction we just signed is already on
   * chain (we still need its signature to poll for confirmation).
   */
  private extractSignatureFromSignedTx(
    signedTx: Transaction | VersionedTransaction
  ): string | null {
    try {
      // Lazily reuse anchor's bundled bs58 encoder — avoids adding a new dependency
      const bs58 = (anchor as any).utils?.bytes?.bs58
      if (!bs58 || typeof bs58.encode !== 'function') {
        return null
      }

      if (signedTx instanceof VersionedTransaction) {
        const sig = signedTx.signatures?.[0]
        if (!sig) return null
        return bs58.encode(sig instanceof Uint8Array ? sig : new Uint8Array(sig))
      }

      // Legacy Transaction: use the SDK's own helper when present, otherwise bs58 the first sig
      const legacy = signedTx as Transaction
      if (typeof (legacy as any).signature?.length === 'number') {
        return bs58.encode((legacy as any).signature)
      }
      const first = legacy.signatures?.[0]?.signature
      return first ? bs58.encode(first as Uint8Array) : null
    } catch (err) {
      console.warn('[anchorClient] Failed to extract signature from signed tx:', err)
      return null
    }
  }

  /**
   * Tests RPC connection by fetching the program account
   */
  async testRpcConnection(): Promise<void> {
    try {
      const programId = getCTOProgramId()
      const accountInfo = await this.connection.getAccountInfo(programId)
      console.log('[anchorClient] RPC test:', accountInfo ? 'Account found' : 'Account not found')
    } catch (error) {
      console.error('[anchorClient] RPC test failed:', error)
    }
  }

  /**
   * Checks if the CTO Pools program is deployed and executable
   */
  async checkContractDeployment(): Promise<ContractStatus> {
    try {
      const programId = getCTOProgramId()
      
      if (!this.program) {
        this.contractStatus = {
          isDeployed: false,
          error: 'Program not initialized',
          fallbackMode: true,
          lastChecked: Date.now()
        }
        return this.contractStatus
      }

      const programAccount = await this.connection.getAccountInfo(programId)
      
      if (!programAccount) {
        this.contractStatus = {
          isDeployed: false,
          error: 'Program account not found',
          fallbackMode: true,
          lastChecked: Date.now()
        }
      } else if (!programAccount.executable) {
        this.contractStatus = {
          isDeployed: false,
          error: 'Account is not executable',
          fallbackMode: true,
          lastChecked: Date.now()
        }
      } else {
        this.contractStatus = {
          isDeployed: true,
          fallbackMode: false,
          lastChecked: Date.now()
        }
      }
      
      return this.contractStatus
      
    } catch (error: any) {
      this.contractStatus = {
        isDeployed: false,
        error: error.message || 'Unknown error',
        fallbackMode: true,
        lastChecked: Date.now()
      }
      return this.contractStatus
    }
  }

  /**
   * Creates mock V3.1 pool data for fallback mode
   */
  private createMockPool(tokenMint: PublicKey): Pool {
    const cacheKey = `pool_${tokenMint.toBase58()}`
    
    if (this.mockDataCache.has(cacheKey)) {
      return this.mockDataCache.get(cacheKey)
    }

    const mockPool: Pool = {
      tokenMint,
      authority: SystemProgram.programId,
      creator: SystemProgram.programId,
      totalShares: new BN(450000000000),
      // LST-based fields
      totalPoolTokens: new BN(400000000000),  // ~400 jitoSOL
      reservedPoolTokens: new BN(75000000000), // ~75 jitoSOL reserved
      totalSpentLamports: new BN(125000000000),
      protocolFeeBps: PROTOCOL_FEE_BPS,
      quorumBps: QUORUM_BPS,
      minProposerDepositLamports: new BN(MIN_PROPOSER_DEPOSIT_LAMPORTS),
      devFeeWallet: SystemProgram.programId,
      burnTokenMint: SystemProgram.programId,
      activeProposal: SystemProgram.programId,
      proposalCount: new BN(5),
      proposalCooldownUntilTs: new BN(0),
      basePenaltyLamports: new BN(100000000), // 0.1 SOL
      // Recovery fields
      activeRecovery: null,
      recoveryCount: new BN(0),
      stakePoolProgram: JITO_STAKE_POOL_PROGRAM,
      stakePool: JITO_STAKE_POOL,
      lstMint: JITO_LST_MINT,
      // V3.1: PumpSwap configuration
      pumpswapEnabled: false,
      pumpswapPoolId: SystemProgram.programId,
      pumpswapBaseVault: SystemProgram.programId,
      pumpswapQuoteVault: SystemProgram.programId,
      pumpswapGlobalConfig: SystemProgram.programId,
      pumpswapFeeRecipient: SystemProgram.programId,
      pumpswapVersion: 0,
      // Raydium config
      raydiumPoolId: SystemProgram.programId,
      raydiumEnabled: false,
      // V3.1: Reentrancy guard
      locked: false
    }

    this.mockDataCache.set(cacheKey, mockPool)
    return mockPool
  }

  /**
   * Creates mock donors for fallback mode (V3.1 updated)
   */
  private createMockDonors(poolPDA: PublicKey): Array<{ account: Donor; publicKey: PublicKey }> {
    const cacheKey = `donors_${poolPDA.toBase58()}`
    
    if (this.mockDataCache.has(cacheKey)) {
      return this.mockDataCache.get(cacheKey)
    }

    const mockDonors = [
      {
        account: {
          pool: poolPDA,
          wallet: SystemProgram.programId,
          shares: new BN(150000000000),
          totalDepositedLamports: new BN(145000000000),
          lastSharesChangeSlot: new BN(234567890),
          // V3.1: Strike tracking
          abortStrikeCount: 0,
          proposeStrikeCount: 0,
          nonAbortParticipationCount: 0,
          nonProposeParticipationCount: 0
        },
        publicKey: SystemProgram.programId
      },
      {
        account: {
          pool: poolPDA,
          wallet: SystemProgram.programId,
          shares: new BN(120000000000),
          totalDepositedLamports: new BN(118000000000),
          lastSharesChangeSlot: new BN(234567880),
          abortStrikeCount: 0,
          proposeStrikeCount: 0,
          nonAbortParticipationCount: 0,
          nonProposeParticipationCount: 0
        },
        publicKey: SystemProgram.programId
      },
      {
        account: {
          pool: poolPDA,
          wallet: SystemProgram.programId,
          shares: new BN(90000000000),
          totalDepositedLamports: new BN(92000000000),
          lastSharesChangeSlot: new BN(234567870),
          abortStrikeCount: 0,
          proposeStrikeCount: 0,
          nonAbortParticipationCount: 0,
          nonProposeParticipationCount: 0
        },
        publicKey: SystemProgram.programId
      }
    ]

    this.mockDataCache.set(cacheKey, mockDonors)
    return mockDonors
  }

  /**
   * Creates mock V3.1 proposal for fallback mode
   */
  private createMockProposal(poolPDA: PublicKey): Proposal {
    const cacheKey = `proposal_${poolPDA.toBase58()}`
    
    if (this.mockDataCache.has(cacheKey)) {
      return this.mockDataCache.get(cacheKey)
    }

    const now = Math.floor(Date.now() / 1000)
    const mockProposal: Proposal = {
      pool: poolPDA,
      kind: { payout: {} },
      proposerWallet: SystemProgram.programId,
      requestedLamports: new BN(75000000000),
      destinationWallet: SystemProgram.programId,
      title: 'Mock Community Proposal',
      description: 'This is a mock proposal for demonstration purposes.',
      createdAtTs: new BN(now - 3600 * 12),
      deadlineTs: new BN(now + 3600 * 60),
      snapshotSlot: new BN(234567890),
      totalSnapshotShares: new BN(450000000000),
      lockedPoolTokens: new BN(70000000000), // jitoSOL locked for this proposal
      yesWeight: new BN(200000000000),
      noWeight: new BN(80000000000),
      abstainWeight: new BN(30000000000),
      participationWeight: new BN(310000000000),
      // V3.1: Abort voting fields
      abortVoter1: PublicKey.default,
      abortVoter2: PublicKey.default,
      abortCount: 0,
      status: { active: {} }
    }

    this.mockDataCache.set(cacheKey, mockProposal)
    return mockProposal
  }

  /**
   * Creates mock user donor for fallback mode (returns null - user not a donor)
   */
  private createMockUserDonor(_poolPDA: PublicKey, _userWallet: PublicKey): Donor | null {
    return null
  }

  // ==========================================================================
  //  PDA Derivation Functions
  // ==========================================================================

  /**
   * Derives the Pool PDA for a given token mint
   */
  getPoolPDA(tokenMint: PublicKey): [PublicKey, number] {
    return PublicKey.findProgramAddressSync(
      [Buffer.from('pool'), tokenMint.toBuffer()],
      getCTOProgramId()
    )
  }

  /**
   * Derives the Donor PDA for a given pool and wallet
   */
  getDonorPDA(pool: PublicKey, wallet: PublicKey): [PublicKey, number] {
    return PublicKey.findProgramAddressSync(
      [Buffer.from('donor'), pool.toBuffer(), wallet.toBuffer()],
      getCTOProgramId()
    )
  }

  /**
   * Derives the Proposal PDA for a given pool and proposal count
   */
  getProposalPDA(pool: PublicKey, proposalCount: BN): [PublicKey, number] {
    return PublicKey.findProgramAddressSync(
      [Buffer.from('proposal'), pool.toBuffer(), proposalCount.toArrayLike(Buffer, 'le', 8)],
      getCTOProgramId()
    )
  }

  /**
   * Derives the Recovery Proposal PDA
   */
  getRecoveryPDA(pool: PublicKey, recoveryCount: BN): [PublicKey, number] {
    return PublicKey.findProgramAddressSync(
      [Buffer.from('recovery'), pool.toBuffer(), recoveryCount.toArrayLike(Buffer, 'le', 8)],
      getCTOProgramId()
    )
  }

  /**
   * Derives the VoteRecord PDA for a given proposal and voter
   */
  getVoteRecordPDA(proposal: PublicKey, voter: PublicKey): [PublicKey, number] {
    return PublicKey.findProgramAddressSync(
      [Buffer.from('vote'), proposal.toBuffer(), voter.toBuffer()],
      getCTOProgramId()
    )
  }

  /**
   * Derives the stake pool withdraw authority PDA
   */
  getStakePoolWithdrawAuthority(stakePool: PublicKey, stakePoolProgram: PublicKey): [PublicKey, number] {
    return PublicKey.findProgramAddressSync(
      [stakePool.toBuffer(), Buffer.from('withdraw')],
      stakePoolProgram
    )
  }

  // ==========================================================================
  //  Stake Pool Helper Functions
  // ==========================================================================

  /**
   * Fetches and derives all accounts needed for stake pool operations
   * This is used for donate_sol and withdraw_sol instructions
   */
  async getStakePoolAccounts(pool: Pool): Promise<{
    stakePoolProgram: PublicKey
    stakePool: PublicKey
    stakePoolWithdrawAuthority: PublicKey
    reserveStake: PublicKey
    managerFeeAccount: PublicKey
    referrerPoolTokensAccount: PublicKey
    poolLstAccount: PublicKey
    lstMint: PublicKey
  }> {
    const stakePoolProgram = pool.stakePoolProgram
    const stakePool = pool.stakePool
    const lstMint = pool.lstMint

    // Derive stake pool withdraw authority
    const [stakePoolWithdrawAuthority] = this.getStakePoolWithdrawAuthority(stakePool, stakePoolProgram)

    // Fetch stake pool account to get reserve stake and other addresses
    const reserveStake = await this.getReserveStakeAddress(stakePool, stakePoolProgram)
    const managerFeeAccount = await this.getManagerFeeAccount(stakePool, lstMint)

    // Pool's LST token account (ATA)
    const [poolPDA] = this.getPoolPDA(pool.tokenMint)
    const poolLstAccount = getAssociatedTokenAddressSync(lstMint, poolPDA, true)

    return {
      stakePoolProgram,
      stakePool,
      stakePoolWithdrawAuthority,
      reserveStake,
      managerFeeAccount,
      referrerPoolTokensAccount: managerFeeAccount, // Same as manager fee for Jito
      poolLstAccount,
      lstMint
    }
  }

  /**
   * Gets the reserve stake account for a stake pool
   * Parses the stake pool account data to get the reserve_stake address
   */
  async getReserveStakeAddress(stakePool: PublicKey, stakePoolProgram: PublicKey): Promise<PublicKey> {
    try {
      const stakePoolAccount = await this.connection.getAccountInfo(stakePool)
      if (stakePoolAccount && stakePoolAccount.data.length >= 162) {
        // reserve_stake is at offset 130-162 in SPL stake pool data
        const reserveStake = new PublicKey(stakePoolAccount.data.slice(130, 162))
        console.log('[anchorClient] Parsed reserve_stake from stake pool data:', reserveStake.toBase58())
        return reserveStake
      } else {
        console.warn('[anchorClient] Stake pool account not found or too small, data length:', stakePoolAccount?.data.length)
      }
    } catch (error) {
      console.error('[anchorClient] Error parsing reserve_stake from stake pool:', error)
    }
    
    // Fallback to PDA derivation (may not work for all stake pools)
    console.warn('[anchorClient] Using PDA fallback for reserve_stake - this may be incorrect')
    const [reserveStake] = PublicKey.findProgramAddressSync(
      [stakePool.toBuffer(), Buffer.from('reserve')],
      stakePoolProgram
    )
    return reserveStake
  }

  /**
   * Gets the manager fee account for a stake pool
   * Parses the stake pool account data to get the manager_fee_account address
   */
  async getManagerFeeAccount(stakePool: PublicKey, lstMint: PublicKey): Promise<PublicKey> {
    try {
      const stakePoolAccount = await this.connection.getAccountInfo(stakePool)
      if (stakePoolAccount && stakePoolAccount.data.length >= 226) {
        // manager_fee_account is at offset 194-226 in SPL stake pool data
        const managerFeeAccount = new PublicKey(stakePoolAccount.data.slice(194, 226))
        console.log('[anchorClient] Parsed manager_fee_account from stake pool data:', managerFeeAccount.toBase58())
        return managerFeeAccount
      } else {
        console.warn('[anchorClient] Stake pool account not found or too small for manager_fee_account, data length:', stakePoolAccount?.data.length)
      }
    } catch (error) {
      console.error('[anchorClient] Error parsing manager_fee_account from stake pool:', error)
    }
    
    // Fallback: return the stake pool withdraw authority's ATA for the LST mint
    console.warn('[anchorClient] Using ATA fallback for manager_fee_account - this may be incorrect')
    const [withdrawAuth] = this.getStakePoolWithdrawAuthority(stakePool, JITO_STAKE_POOL_PROGRAM)
    return getAssociatedTokenAddressSync(lstMint, withdrawAuth, true)
  }

  // ==========================================================================
  //  V3.1 Instruction Methods
  // ==========================================================================

  /**
   * Creates a new pool with stake pool configuration
   * Uses fresh blockhash handling to prevent simulation failures
   */
  async createPool(
    tokenMint: PublicKey,
    devFeeWallet: PublicKey,
    burnTokenMint: PublicKey,
    stakePoolProgram: PublicKey = JITO_STAKE_POOL_PROGRAM,
    stakePool: PublicKey = JITO_STAKE_POOL,
    lstMint: PublicKey = JITO_LST_MINT
  ) {
    if (!this.wallet.publicKey) {
      throw new Error('Wallet not connected')
    }

    await this.checkContractDeployment()
    
    if (this.contractStatus.fallbackMode) {
      throw new Error('Smart contract is not deployed.')
    }

    if (!this.program) {
      throw new Error('Program not initialized')
    }

    const [pool] = this.getPoolPDA(tokenMint)

    // Build the instruction with proper accounts
    const ix = await (this.program.methods as any)
      .createPool(devFeeWallet, burnTokenMint, stakePoolProgram, stakePool, lstMint)
      .accounts({
        pool,
        tokenMint,
        creator: this.wallet.publicKey,
        systemProgram: SystemProgram.programId,
      } as any)
      .instruction()

    // Get fresh blockhash
    const { blockhash } = await this.getFreshBlockhash()

    // Create Versioned Transaction
    const messageV0 = new TransactionMessage({
      payerKey: this.wallet.publicKey,
      recentBlockhash: blockhash,
      instructions: [ix],
    }).compileToV0Message()
    
    const tx = new VersionedTransaction(messageV0)

    // Sign and send with fresh blockhash and simulation
    return await this.signAndSendTransaction(tx)
  }

  /**
   * Donate SOL to a pool via stake pool CPI
   * SOL is converted to jitoSOL (LST) and shares are minted to the donor
   * Uses fresh blockhash handling to prevent "Blockhash not found" errors
   */
  async donateSol(tokenMint: PublicKey, lamportsIn: BN, minimumPoolTokensOut: BN = new BN(0)) {
    if (!this.wallet.publicKey) {
      throw new Error('Wallet not connected')
    }

    await this.checkContractDeployment()
    if (this.contractStatus.fallbackMode) {
      throw new Error('Smart contract is not deployed.')
    }

    if (!this.program) {
      throw new Error('Program not initialized')
    }

    const [poolPDA] = this.getPoolPDA(tokenMint)
    const [donor] = this.getDonorPDA(poolPDA, this.wallet.publicKey)

    // Fetch pool to get stake pool config
    const poolAccount = await (this.program.account as any).pool.fetch(poolPDA)
    const pool = poolAccount as unknown as Pool
    
    // Get all stake pool accounts
    const stakePoolAccounts = await this.getStakePoolAccounts(pool)

    // Build the instruction with proper accounts
    const ix = await (this.program.methods as any)
      .donateSol(lamportsIn, minimumPoolTokensOut)
      .accounts({
        pool: poolPDA,
        donor,
        donorWallet: this.wallet.publicKey,
        stakePoolProgram: stakePoolAccounts.stakePoolProgram,
        stakePool: stakePoolAccounts.stakePool,
        stakePoolWithdrawAuthority: stakePoolAccounts.stakePoolWithdrawAuthority,
        reserveStake: stakePoolAccounts.reserveStake,
        managerFeeAccount: stakePoolAccounts.managerFeeAccount,
        referrerPoolTokensAccount: stakePoolAccounts.referrerPoolTokensAccount,
        poolLstAccount: stakePoolAccounts.poolLstAccount,
        lstMint: stakePoolAccounts.lstMint,
        tokenProgram: TOKEN_PROGRAM_ID,
        associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
        systemProgram: SystemProgram.programId,
      } as any)
      .instruction()

    // Get fresh blockhash
    const { blockhash } = await this.getFreshBlockhash()

    // Create Versioned Transaction
    const messageV0 = new TransactionMessage({
      payerKey: this.wallet.publicKey,
      recentBlockhash: blockhash,
      instructions: [ix],
    }).compileToV0Message()
    
    const tx = new VersionedTransaction(messageV0)

    // Sign and send with fresh blockhash and simulation
    return await this.signAndSendTransaction(tx)
  }

  /**
   * Withdraw SOL from the pool via stake pool CPI
   * jitoSOL is converted back to SOL and returned to the donor
   * Uses fresh blockhash handling to prevent simulation failures
   */
  async withdrawSol(tokenMint: PublicKey, lamportsOutDesired: BN, minimumLamportsOut: BN = new BN(0)) {
    if (!this.wallet.publicKey) {
      throw new Error('Wallet not connected')
    }

    await this.checkContractDeployment()
    if (this.contractStatus.fallbackMode) {
      throw new Error('Smart contract is not deployed.')
    }

    if (!this.program) {
      throw new Error('Program not initialized')
    }

    const [poolPDA] = this.getPoolPDA(tokenMint)
    const [donor] = this.getDonorPDA(poolPDA, this.wallet.publicKey)

    // Fetch pool to get stake pool config
    const poolAccount = await (this.program.account as any).pool.fetch(poolPDA)
    const pool = poolAccount as unknown as Pool
    
    // Get all stake pool accounts
    const stakePoolAccounts = await this.getStakePoolAccounts(pool)

    // Build the instruction with proper accounts
    const ix = await (this.program.methods as any)
      .withdrawSol(lamportsOutDesired, minimumLamportsOut)
      .accounts({
        pool: poolPDA,
        donor,
        donorWallet: this.wallet.publicKey,
        stakePoolProgram: stakePoolAccounts.stakePoolProgram,
        stakePool: stakePoolAccounts.stakePool,
        stakePoolWithdrawAuthority: stakePoolAccounts.stakePoolWithdrawAuthority,
        reserveStake: stakePoolAccounts.reserveStake,
        managerFeeAccount: stakePoolAccounts.managerFeeAccount,
        poolLstAccount: stakePoolAccounts.poolLstAccount,
        lstMint: stakePoolAccounts.lstMint,
        clock: SYSVAR_CLOCK_PUBKEY,
        stakeHistory: SYSVAR_STAKE_HISTORY_PUBKEY,
        stakeProgram: STAKE_PROGRAM_ID,
        tokenProgram: TOKEN_PROGRAM_ID,
        systemProgram: SystemProgram.programId,
      } as any)
      .instruction()

    // Get fresh blockhash
    const { blockhash } = await this.getFreshBlockhash()

    // Create Versioned Transaction
    const messageV0 = new TransactionMessage({
      payerKey: this.wallet.publicKey,
      recentBlockhash: blockhash,
      instructions: [ix],
    }).compileToV0Message()
    
    const tx = new VersionedTransaction(messageV0)

    // Sign and send with fresh blockhash and simulation
    return await this.signAndSendTransaction(tx)
  }

  /**
   * Create a proposal with title and description
   * Uses fresh blockhash handling to prevent simulation failures
   */
  async createProposal(
    tokenMint: PublicKey,
    requestedLamports: BN,
    destinationWallet: PublicKey,
    title: string,
    description: string
  ) {
    if (!this.wallet.publicKey) {
      throw new Error('Wallet not connected')
    }

    await this.checkContractDeployment()
    if (this.contractStatus.fallbackMode) {
      throw new Error('Smart contract is not deployed.')
    }

    if (!this.program) {
      throw new Error('Program not initialized')
    }

    const [poolPDA] = this.getPoolPDA(tokenMint)
    
    const poolAccount = await (this.program.account as any).pool.fetch(poolPDA)
    const pool = poolAccount as unknown as Pool
    const proposalCount = pool.proposalCount
    
    const [donor] = this.getDonorPDA(poolPDA, this.wallet.publicKey)
    const [proposal] = this.getProposalPDA(poolPDA, proposalCount)

    // Get stake pool accounts for reading exchange rate
    const poolLstAccount = getAssociatedTokenAddressSync(pool.lstMint, poolPDA, true)

    // Build the instruction with proper accounts
    const ix = await (this.program.methods as any)
      .createProposal(requestedLamports, destinationWallet, title, description)
      .accounts({
        pool: poolPDA,
        donor,
        proposal,
        proposerWallet: this.wallet.publicKey,
        stakePool: pool.stakePool,
        poolLstAccount,
        lstMint: pool.lstMint,
        systemProgram: SystemProgram.programId,
      } as any)
      .instruction()

    // Get fresh blockhash
    const { blockhash } = await this.getFreshBlockhash()

    // Create Versioned Transaction
    const messageV0 = new TransactionMessage({
      payerKey: this.wallet.publicKey,
      recentBlockhash: blockhash,
      instructions: [ix],
    }).compileToV0Message()
    
    const tx = new VersionedTransaction(messageV0)

    // Sign and send with fresh blockhash and simulation
    return await this.signAndSendTransaction(tx)
  }

  /**
   * V3.1: Vote on an active proposal with support for Yes, No, Abstain, and Abort choices
   * Uses fresh blockhash handling to prevent simulation failures
   */
  async vote(tokenMint: PublicKey, choice: VoteChoice) {
    if (!this.wallet.publicKey) {
      throw new Error('Wallet not connected')
    }

    await this.checkContractDeployment()
    if (this.contractStatus.fallbackMode) {
      throw new Error('Smart contract is not deployed.')
    }

    if (!this.program) {
      throw new Error('Program not initialized')
    }

    const [poolPDA] = this.getPoolPDA(tokenMint)
    
    const poolAccount = await (this.program.account as any).pool.fetch(poolPDA) as any
    if (!poolAccount.activeProposal) {
      throw new Error('No active proposal to vote on')
    }
    
    const proposal = poolAccount.activeProposal
    const [donor] = this.getDonorPDA(poolPDA, this.wallet.publicKey)
    const [voteRecord] = this.getVoteRecordPDA(proposal, this.wallet.publicKey)

    // V3.1: Build vote choice object including abort option
    let voteChoice: any
    switch (choice) {
      case VoteChoice.Yes:
        voteChoice = { yes: {} }
        break
      case VoteChoice.No:
        voteChoice = { no: {} }
        break
      case VoteChoice.Abstain:
        voteChoice = { abstain: {} }
        break
      case VoteChoice.Abort:
        voteChoice = { abort: {} }
        break
    }

    // Build the instruction with proper accounts
    const ix = await (this.program.methods as any)
      .vote(voteChoice)
      .accounts({
        pool: poolPDA,
        proposal,
        donor,
        voteRecord,
        voterWallet: this.wallet.publicKey,
        systemProgram: SystemProgram.programId,
      } as any)
      .instruction()

    // Get fresh blockhash
    const { blockhash } = await this.getFreshBlockhash()

    // Create Versioned Transaction
    const messageV0 = new TransactionMessage({
      payerKey: this.wallet.publicKey,
      recentBlockhash: blockhash,
      instructions: [ix],
    }).compileToV0Message()
    
    const tx = new VersionedTransaction(messageV0)

    // Sign and send with fresh blockhash and simulation
    return await this.signAndSendTransaction(tx)
  }

  /**
   * V3.1: Finalize an abort after 2 eligible wallets have voted Abort
   * This unlocks reserved liquidity, marks proposal as Aborted, and increments proposer strike
   */
  async abortProposal(tokenMint: PublicKey) {
    if (!this.wallet.publicKey) {
      throw new Error('Wallet not connected')
    }

    await this.checkContractDeployment()
    if (this.contractStatus.fallbackMode) {
      throw new Error('Smart contract is not deployed.')
    }

    if (!this.program) {
      throw new Error('Program not initialized')
    }

    const [poolPDA] = this.getPoolPDA(tokenMint)
    
    const poolAccount = await (this.program.account as any).pool.fetch(poolPDA) as any
    if (!poolAccount.activeProposal) {
      throw new Error('No active proposal to abort')
    }
    
    const proposalPubkey = poolAccount.activeProposal
    const proposalAccount = await (this.program.account as any).proposal.fetch(proposalPubkey) as unknown as Proposal
    
    const [aborter] = this.getDonorPDA(poolPDA, this.wallet.publicKey)
    const [proposerDonor] = this.getDonorPDA(poolPDA, proposalAccount.proposerWallet)

    // Build the instruction with proper accounts
    const ix = await (this.program.methods as any)
      .abortProposal()
      .accounts({
        pool: poolPDA,
        proposal: proposalPubkey,
        aborter,
        aborterWallet: this.wallet.publicKey,
        proposerDonor,
        systemProgram: SystemProgram.programId,
      } as any)
      .instruction()

    // Get fresh blockhash
    const { blockhash } = await this.getFreshBlockhash()

    // Create Versioned Transaction
    const messageV0 = new TransactionMessage({
      payerKey: this.wallet.publicKey,
      recentBlockhash: blockhash,
      instructions: [ix],
    }).compileToV0Message()
    
    const tx = new VersionedTransaction(messageV0)

    // Sign and send with fresh blockhash and simulation
    return await this.signAndSendTransaction(tx)
  }

  /**
   * V3.1: Configure PumpSwap pool for buy & burn (authority only)
   * Stores all critical PumpSwap addresses in the Pool account for secure execution
   */
  async configurePumpswapPool(
    tokenMint: PublicKey,
    pumpswapPoolId: PublicKey,
    baseVault: PublicKey,
    quoteVault: PublicKey,
    globalConfig: PublicKey,
    feeRecipient: PublicKey,
    enabled: boolean
  ) {
    if (!this.wallet.publicKey) {
      throw new Error('Wallet not connected')
    }

    await this.checkContractDeployment()
    if (this.contractStatus.fallbackMode) {
      throw new Error('Smart contract is not deployed.')
    }

    if (!this.program) {
      throw new Error('Program not initialized')
    }

    const [poolPDA] = this.getPoolPDA(tokenMint)

    // Build the instruction with proper accounts
    const ix = await (this.program.methods as any)
      .configurePumpswapPool(pumpswapPoolId, baseVault, quoteVault, globalConfig, feeRecipient, enabled)
      .accounts({
        pool: poolPDA,
        authority: this.wallet.publicKey,
      } as any)
      .instruction()

    // Get fresh blockhash
    const { blockhash } = await this.getFreshBlockhash()

    // Create Versioned Transaction
    const messageV0 = new TransactionMessage({
      payerKey: this.wallet.publicKey,
      recentBlockhash: blockhash,
      instructions: [ix],
    }).compileToV0Message()
    
    const tx = new VersionedTransaction(messageV0)

    // Sign and send with fresh blockhash and simulation
    return await this.signAndSendTransaction(tx)
  }

  /**
   * Execute a proposal (complex instruction with stake pool and swap accounts)
   * Uses fresh blockhash handling to prevent simulation failures
   */
  async executeProposal(
    tokenMint: PublicKey,
    minimumLamportsOut: BN = new BN(0)
  ) {
    await this.checkContractDeployment()
    if (this.contractStatus.fallbackMode) {
      throw new Error('Smart contract is not deployed.')
    }

    if (!this.program) {
      throw new Error('Program not initialized')
    }

    if (!this.wallet.publicKey) {
      throw new Error('Wallet not connected')
    }

    const [poolPDA] = this.getPoolPDA(tokenMint)
    
    const poolAccount = await (this.program.account as any).pool.fetch(poolPDA) as unknown as Pool
    if (!poolAccount.activeProposal) {
      throw new Error('No active proposal to execute')
    }
    
    const proposalPubkey = poolAccount.activeProposal
    const proposalAccount = await (this.program.account as any).proposal.fetch(proposalPubkey) as unknown as Proposal

    // Get stake pool accounts
    const stakePoolAccounts = await this.getStakePoolAccounts(poolAccount)

    // Note: Execute proposal requires many swap accounts that are complex to derive
    // This is a placeholder - full implementation would need swap pool state parsing
    console.warn('[anchorClient] executeProposal requires swap accounts - needs full implementation')

    // Build the instruction with proper accounts
    const ix = await (this.program.methods as any)
      .executeProposal(minimumLamportsOut)
      .accounts({
        pool: poolPDA,
        proposal: proposalPubkey,
        destinationWallet: proposalAccount.destinationWallet,
        devFeeWallet: poolAccount.devFeeWallet,
        stakePoolProgram: stakePoolAccounts.stakePoolProgram,
        stakePool: stakePoolAccounts.stakePool,
        stakePoolWithdrawAuthority: stakePoolAccounts.stakePoolWithdrawAuthority,
        reserveStake: stakePoolAccounts.reserveStake,
        managerFeeAccount: stakePoolAccounts.managerFeeAccount,
        poolLstAccount: stakePoolAccounts.poolLstAccount,
        lstMint: stakePoolAccounts.lstMint,
        clock: SYSVAR_CLOCK_PUBKEY,
        stakeHistory: SYSVAR_STAKE_HISTORY_PUBKEY,
        stakeProgram: STAKE_PROGRAM_ID,
        systemProgram: SystemProgram.programId,
        // PumpSwap accounts (when enabled)
        pumpswapProgram: poolAccount.pumpswapEnabled ? PUMPSWAP_PROGRAM_ID : SystemProgram.programId,
        pumpswapPool: poolAccount.pumpswapEnabled ? poolAccount.pumpswapPoolId : SystemProgram.programId,
        pumpswapGlobalConfig: poolAccount.pumpswapEnabled ? poolAccount.pumpswapGlobalConfig : SystemProgram.programId,
        pumpswapProtocolFeeRecipient: poolAccount.pumpswapEnabled ? poolAccount.pumpswapFeeRecipient : SystemProgram.programId,
        pumpswapProtocolFeeRecipientTokenAccount: SystemProgram.programId, // Derived from fee recipient
        pumpswapEventAuthority: SystemProgram.programId, // PDA derived from pumpswap program
        pumpswapPoolBaseVault: poolAccount.pumpswapEnabled ? poolAccount.pumpswapBaseVault : SystemProgram.programId,
        pumpswapPoolQuoteVault: poolAccount.pumpswapEnabled ? poolAccount.pumpswapQuoteVault : SystemProgram.programId,
        pumpswapBaseTokenProgram: TOKEN_PROGRAM_ID,
        pumpswapQuoteTokenProgram: TOKEN_PROGRAM_ID,
        // Raydium accounts (legacy - placeholder with system program)
        raydiumProgram: SystemProgram.programId,
        raydiumPool: SystemProgram.programId,
        raydiumPoolAuthority: SystemProgram.programId,
        raydiumOpenOrders: SystemProgram.programId,
        raydiumTargetOrders: SystemProgram.programId,
        raydiumCoinVault: SystemProgram.programId,
        raydiumPcVault: SystemProgram.programId,
        serumProgram: SystemProgram.programId,
        serumMarket: SystemProgram.programId,
        serumBids: SystemProgram.programId,
        serumAsks: SystemProgram.programId,
        serumEventQueue: SystemProgram.programId,
        serumCoinVault: SystemProgram.programId,
        serumPcVault: SystemProgram.programId,
        serumVaultSigner: SystemProgram.programId,
        poolWsolAccount: SystemProgram.programId,
        poolCtopAccount: SystemProgram.programId,
        incineratorCtopAccount: SystemProgram.programId,
        wsolMint: new PublicKey('So11111111111111111111111111111111111111112'),
        ctopMint: poolAccount.burnTokenMint,
        incinerator: new PublicKey('1nc1nerator11111111111111111111111111111111'),
        tokenProgram: TOKEN_PROGRAM_ID,
        associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
        rent: SYSVAR_RENT_PUBKEY,
        executor: this.wallet.publicKey,
      } as any)
      .instruction()

    // Get fresh blockhash
    const { blockhash } = await this.getFreshBlockhash()

    // Create Versioned Transaction
    const messageV0 = new TransactionMessage({
      payerKey: this.wallet.publicKey,
      recentBlockhash: blockhash,
      instructions: [ix],
    }).compileToV0Message()
    
    const tx = new VersionedTransaction(messageV0)

    // Sign and send with fresh blockhash and simulation
    return await this.signAndSendTransaction(tx)
  }

  // ==========================================================================
  //  V3.1 Fetch Methods
  // ==========================================================================

  /**
   * Fetches a Pool account by token mint
   */
  async fetchPool(tokenMint: PublicKey): Promise<Pool | null> {
    try {
      await this.checkContractDeployment()
      
      if (this.contractStatus.fallbackMode) {
        return this.createMockPool(tokenMint)
      }

      if (!this.program) {
        return null
      }

      const [poolPDA] = this.getPoolPDA(tokenMint)
      const poolAccount = await (this.program.account as any).pool.fetch(poolPDA)
      return poolAccount as unknown as Pool
    } catch (error) {
      if (this.contractStatus.isDeployed) {
        return null
      } else {
        return this.createMockPool(tokenMint)
      }
    }
  }

  /**
   * Fetches a Donor account for a pool and wallet
   */
  async fetchDonor(pool: PublicKey, wallet: PublicKey): Promise<Donor | null> {
    try {
      await this.checkContractDeployment()
      
      if (this.contractStatus.fallbackMode) {
        return this.createMockUserDonor(pool, wallet)
      }

      if (!this.program) {
        return null
      }

      const [donorPDA] = this.getDonorPDA(pool, wallet)
      const donorAccount = await (this.program.account as any).donor.fetch(donorPDA)
      return donorAccount as unknown as Donor
    } catch (error) {
      if (this.contractStatus.isDeployed) {
        return null
      } else {
        return this.createMockUserDonor(pool, wallet)
      }
    }
  }

  /**
   * Fetches the active Proposal for a pool
   */
  async fetchProposal(pool: PublicKey): Promise<Proposal | null> {
    try {
      await this.checkContractDeployment()
      
      if (this.contractStatus.fallbackMode) {
        return this.createMockProposal(pool)
      }

      if (!this.program) {
        return null
      }

      const poolAccount = await (this.program.account as any).pool.fetch(pool) as any
      if (!poolAccount.activeProposal) {
        return null
      }

      const proposalAccount = await (this.program.account as any).proposal.fetch(poolAccount.activeProposal)
      return proposalAccount as unknown as Proposal
    } catch (error) {
      if (this.contractStatus.isDeployed) {
        return null
      } else {
        return this.createMockProposal(pool)
      }
    }
  }

  /**
   * Fetches a VoteRecord for a proposal and voter
   */
  async fetchVoteRecord(proposal: PublicKey, voter: PublicKey): Promise<VoteRecord | null> {
    try {
      await this.checkContractDeployment()
      
      if (this.contractStatus.fallbackMode) {
        return null
      }

      if (!this.program) {
        return null
      }

      const [voteRecordPDA] = this.getVoteRecordPDA(proposal, voter)
      const voteRecord = await (this.program.account as any).voteRecord.fetch(voteRecordPDA)
      return voteRecord as unknown as VoteRecord
    } catch (error) {
      return null
    }
  }

  /**
   * Fetches all pools from the program
   */
  async getAllPools(): Promise<{ account: Pool; publicKey: PublicKey }[]> {
    try {
      await this.checkContractDeployment()
      
      if (this.contractStatus.fallbackMode) {
        const mockTokenMint = SystemProgram.programId
        const mockPool = this.createMockPool(mockTokenMint)
        const [poolPDA] = this.getPoolPDA(mockTokenMint)
        
        return [{ account: mockPool, publicKey: poolPDA }]
      }

      if (!this.program) {
        return []
      }

      const pools = await (this.program.account as any).pool.all()
      return pools.map((pool: any) => ({
        account: pool.account as unknown as Pool,
        publicKey: pool.publicKey
      }))
    } catch (error) {
      if (this.contractStatus.isDeployed) {
        return []
      } else {
        const mockTokenMint = SystemProgram.programId
        const mockPool = this.createMockPool(mockTokenMint)
        const [poolPDA] = this.getPoolPDA(mockTokenMint)
        
        return [{ account: mockPool, publicKey: poolPDA }]
      }
    }
  }

  /**
   * Fetches all donors for a specific pool
   */
  async getPoolDonors(pool: PublicKey): Promise<{ account: Donor; publicKey: PublicKey }[]> {
    try {
      await this.checkContractDeployment()
      
      if (this.contractStatus.fallbackMode) {
        return this.createMockDonors(pool)
      }

      if (!this.program) {
        return []
      }

      const donors = await (this.program.account as any).donor.all([
        {
          memcmp: {
            offset: 8,
            bytes: pool.toBase58()
          }
        }
      ])
      return donors.map((donor: any) => ({
        account: donor.account as unknown as Donor,
        publicKey: donor.publicKey
      }))
    } catch (error) {
      if (this.contractStatus.isDeployed) {
        return []
      } else {
        return this.createMockDonors(pool)
      }
    }
  }

  // ==========================================================================
  //  Legacy Compatibility Methods (deprecated, use current methods)
  // ==========================================================================

  /**
   * @deprecated Use donateSol instead
   * Legacy donate method - redirects to donateSol
   */
  async donate(tokenMint: PublicKey, amount: BN) {
    console.warn('[anchorClient] donate() is deprecated, use donateSol() instead')
    return this.donateSol(tokenMint, amount)
  }

  /**
   * @deprecated Use withdrawSol instead
   * Legacy withdraw method - redirects to withdrawSol
   */
  async withdraw(tokenMint: PublicKey, amount?: BN) {
    console.warn('[anchorClient] withdraw() is deprecated, use withdrawSol() instead')
    return this.withdrawSol(tokenMint, amount || new BN(0))
  }
}

/**
 * Factory function to create a CtoPoolsClient instance
 */
export function createCtoPoolsClient(connection: Connection, wallet: WalletContextState): CtoPoolsClient {
  return new CtoPoolsClient(connection, wallet)
}

// ==========================================================================
//  Utility Functions
// ==========================================================================

/**
 * Converts lamports to SOL
 */
export function lamportsToSol(lamports: BN | number): number {
  const lamportsBN = typeof lamports === 'number' ? new BN(lamports) : lamports
  return lamportsBN.toNumber() / LAMPORTS_PER_SOL
}

/**
 * Converts SOL to lamports
 */
export function solToLamports(sol: number): BN {
  return new BN(sol * LAMPORTS_PER_SOL)
}

/**
 * Returns the dev fee wallet public key from environment
 */
export function getDevFeeWallet(): PublicKey {
  return getEnvPublicKey('VITE_APP_DEV_FEE_WALLET')
}

/**
 * Returns the burn token mint public key from environment
 */
export function getBurnTokenMint(): PublicKey {
  return getEnvPublicKey('VITE_APP_BURN_TOKEN_MINT')
}

/**
 * Returns the stake pool program public key from environment
 */
export function getStakePoolProgram(): PublicKey {
  return getEnvPublicKey('VITE_APP_STAKE_POOL_PROGRAM')
}

/**
 * Returns the stake pool public key from environment
 */
export function getStakePool(): PublicKey {
  return getEnvPublicKey('VITE_APP_STAKE_POOL')
}

/**
 * Returns the LST mint (jitoSOL) public key from environment
 */
export function getLstMint(): PublicKey {
  return getEnvPublicKey('VITE_APP_LST_MINT')
}

/**
 * Estimates the SOL value of a given amount of LST (jitoSOL) tokens
 * This uses a simplified exchange rate estimation
 * In production, fetch the actual rate from the stake pool
 */
export function estimateLstToSol(lstAmount: BN, exchangeRate: number = 1.0): number {
  // jitoSOL typically trades at a premium due to staking rewards
  // Exchange rate is typically > 1.0 (e.g., 1 jitoSOL = 1.05 SOL)
  const lstSol = lstAmount.toNumber() / LAMPORTS_PER_SOL
  return lstSol * exchangeRate
}

/**
 * Estimates the LST (jitoSOL) amount for a given SOL value
 */
export function estimateSolToLst(solAmount: number, exchangeRate: number = 1.0): BN {
  const lstAmount = (solAmount / exchangeRate) * LAMPORTS_PER_SOL
  return new BN(Math.floor(lstAmount))
}
